// src/components/compliance/scanGeometry.ts
/**
 * Geometry and protocol of the ID scanner — shared by both threads.
 *
 * Everything in here is plain arithmetic without OpenCV and without the DOM,
 * so it is checkable in a unit test instead of only in front of a real camera.
 *
 * It lives in its own module because both sides need it: the main thread
 * ({@link ./documentScanner}) and the worker ({@link ./documentScanner.worker}).
 * The worker must not reach into `documentScanner.ts` — that module starts the
 * worker, and Vite refuses to bundle a worker that (however indirectly) starts
 * itself («Circular worker imports»). One shared module downstream of both
 * keeps a single source of truth without that cycle.
 *
 * No OpenCV is pulled in here — only the worker may do that, and the grep test
 * in `documentScanner.test.ts` keeps it that way.
 */
import type { CropRect } from './cameraCrop'

/** A corner in the coordinates of the scanned frame. */
export interface ScanPoint {
  x: number
  y: number
}

/** Four corners, sorted: top-left, top-right, bottom-right, bottom-left. */
export type Quad = readonly [ScanPoint, ScanPoint, ScanPoint, ScanPoint]

/** Long edge the detection works on — more pixels cost time without helping. */
export const SCAN_LONG_EDGE_PX = 640

/** Roughly six pictures per second: enough to feel instant, cheap on battery. */
export const SCAN_INTERVAL_MS = 160

/**
 * How long we wait for the scanner worker before it stays off.
 *
 * Generous on purpose since Issue #1141: OpenCV loads in the worker, so while
 * it runs the page stays responsive and the shutter button works. A six-year-old
 * phone needs a good 16 s for the WASM build — cutting it off after ten would
 * deny the scanner to exactly the devices that profit most from it.
 */
export const SCAN_OPENCV_TIMEOUT_MS = 30_000

/**
 * Accepted side ratio of the card. ID-1 is 1.586 (ISO/IEC 7810); held slightly
 * tilted it appears a bit squarer or longer, hence the range around it.
 */
export const SCAN_ASPECT_MIN = 1.35
export const SCAN_ASPECT_MAX = 1.85

/**
 * Share of the frame the card has to cover — otherwise it is too far away.
 *
 * Two thirds instead of the earlier 80 % (Issue #1146): whoever holds the phone
 * in one hand and the card in the other hardly ever fills the frame that far,
 * and a card at 65 % of a 4K still is still far above the resolution the
 * reviewer and the extraction need.
 */
export const SCAN_MIN_FRAME_AREA_SHARE = 0.65

/**
 * Share of the picture a quadrangle has to cover before the detection offers it
 * at all (Issue #1163).
 *
 * The detection used to hand over the largest convex quadrangle it could find,
 * whatever its shape — on a bright table the edge of the table or a sheet of
 * paper won against the card, and the card was never looked at again. A fifth
 * of the picture is deliberately far below {@link SCAN_MIN_FRAME_AREA_SHARE}:
 * the card still has to grow into the frame («Näher heran»), this only sorts
 * out the small contours that cannot be the card in the first place.
 */
export const SCAN_WORKER_MIN_AREA_SHARE = 0.2

/**
 * Canny thresholds of the second pass on a bright background (Issue #1163).
 *
 * The first pass keeps the long-standing 75/200. Where card and surface are
 * almost equally bright the step at the edge is far smaller than that and the
 * contour tears open; the low pair finds it, at the price of a lot more noise —
 * which is why it only runs when the first pass came back empty.
 */
export const SCAN_CANNY_LOW_CONTRAST: readonly [number, number] = [20, 60]

/** Contrast limit of the CLAHE in the second pass — above it, noise explodes. */
export const SCAN_CLAHE_CLIP_LIMIT = 2.0

/** Tiles per side the CLAHE equalises in — 8 × 8 is the OpenCV default. */
export const SCAN_CLAHE_TILE_SIZE = 8

/** How far a corner may stick out of the frame, as a share of the frame size. */
export const SCAN_FRAME_TOLERANCE = 0.06

/**
 * How long the scanner may search before the tip about the surface appears
 * (Issue #1163).
 *
 * Eight seconds is well past the point where the scanner normally catches the
 * card — whoever is still waiting then is holding it onto something the camera
 * cannot separate it from, and is better served by one sentence than by another
 * «Halten Sie den Ausweis in den Rahmen.»
 */
export const SCAN_BACKGROUND_HINT_MS = 8000

/** How long the card has to lie still before the shutter is released. */
export const SCAN_STEADY_MS = 600

/**
 * How far a corner may wander meanwhile, as a share of the frame width.
 *
 * Four percent — roughly 26 px of a 640 px scan frame (Issue #1146). A hand
 * holding a phone shakes by that much all the time; at the earlier 1.5 % the
 * clock restarted on nearly every picture and the shutter never got there.
 */
export const SCAN_STEADY_TOLERANCE = 0.04

/**
 * How many pictures in a row may miss the card before the scanner starts over.
 *
 * A single picture says little: a passing reflection, a blink of motion blur or
 * a finger over the edge and the detection is gone, only to be back on the next
 * one. Dropping the outline and the steady clock right away made the card look
 * «lost» several times a second (Issue #1146).
 */
export const SCAN_MISS_GRACE = 3

/**
 * How many detections the shown outline and the steadiness are averaged over.
 *
 * Four pictures are about two thirds of a second at {@link SCAN_INTERVAL_MS} —
 * long enough to iron out the jitter of a hand, short enough that the outline
 * still follows the card without visible lag.
 */
export const SCAN_SMOOTH_FRAMES = 4

/**
 * How old the last detected quadrangle may be when the shutter is released
 * (Issue #1118). Beyond that the card may have moved since it was measured, so
 * the capture falls back to the plain rectangular crop.
 */
export const SCAN_QUAD_MAX_AGE_MS = 500

/**
 * Longest edge of a straightened capture. An ID-1 card at 2400 px is roughly
 * 700 dpi — far more than any reviewer or extraction needs, and beyond that
 * the perspective correction only invents pixels.
 */
export const WARP_MAX_WIDTH_PX = 2400

/**
 * Margin kept around the card in the straightened picture, per side.
 *
 * The detected outline sits on the edge of the card; without a little air
 * around it a slightly imprecise corner would cut the card off.
 */
export const WARP_MARGIN = 0.02

// ---------------------------------------------------------------------------
// What travels between the threads
// ---------------------------------------------------------------------------

/**
 * Raw pixels, the shape of `ImageData` without needing the class.
 *
 * `ImageData` exists in the browser and in the worker but not in jsdom, and a
 * plain object travels through `postMessage` just as well.
 */
export interface ScanImage {
  readonly data: Uint8ClampedArray
  readonly width: number
  readonly height: number
}

/** What the worker is handed to look at — a bitmap is transferable, so free. */
export type ScanFrame = ImageBitmap | ScanImage

/** A found card, together with the size of the frame it was measured in. */
export interface ScanDetection {
  readonly quad: Quad
  readonly width: number
  readonly height: number
}

/** Main thread → worker. */
export type ScannerRequest =
  | { type: 'detect'; id: number; frame: ScanFrame }
  | { type: 'warp'; id: number; frame: ScanFrame; quad: Quad }

/** Worker → main thread. */
export type ScannerResponse =
  | { type: 'ready' }
  | { type: 'failed' }
  | { type: 'quad'; id: number; detection: ScanDetection | null }
  | { type: 'warped'; id: number; image: ScanImage | null }

/** An unused bitmap holds graphics memory until it is closed. */
export function releaseFrame(frame: ScanFrame): void {
  if ('data' in frame) return
  try {
    frame.close()
  } catch {
    // Already closed, or a browser without `ImageBitmap.close`
  }
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * Four points sorted to top-left, top-right, bottom-right, bottom-left.
 *
 * Sorted by the angle around the centre (clockwise on screen, where y points
 * down) and then rotated so the corner closest to the origin comes first. The
 * usual shortcut over the sums and differences of the coordinates breaks down
 * on a card held at 45°, this does not.
 *
 * `null` unless exactly four points are handed in — a contour with three or
 * five corners is not a card.
 */
export function orderCorners(points: readonly ScanPoint[]): Quad | null {
  if (points.length !== 4) return null

  const centerX = points.reduce((sum, point) => sum + point.x, 0) / 4
  const centerY = points.reduce((sum, point) => sum + point.y, 0) / 4
  const clockwise = [...points].sort(
    (a, b) => Math.atan2(a.y - centerY, a.x - centerX) - Math.atan2(b.y - centerY, b.x - centerX),
  )

  let first = 0
  clockwise.forEach((point, index) => {
    const best = clockwise[first]
    if (point.x + point.y < best.x + best.y) first = index
  })

  return [
    clockwise[first],
    clockwise[(first + 1) % 4],
    clockwise[(first + 2) % 4],
    clockwise[(first + 3) % 4],
  ]
}

/** Distance between two corners. */
export function distance(a: ScanPoint, b: ScanPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/**
 * The same quadrangle in another resolution (Issue #1118).
 *
 * The detection works on a frame scaled down to {@link SCAN_LONG_EDGE_PX}, the
 * capture is straightened on the still picture in sensor resolution — the
 * corners have to travel between the two. Unusable dimensions leave the
 * quadrangle untouched; a wrong factor would be worse than none.
 */
export function scaleQuad(
  quad: Quad,
  fromWidth: number,
  fromHeight: number,
  toWidth: number,
  toHeight: number,
): Quad {
  if (!(fromWidth > 0 && fromHeight > 0)) return quad

  const factorX = toWidth / fromWidth
  const factorY = toHeight / fromHeight
  const moved = quad.map((corner) => ({ x: corner.x * factorX, y: corner.y * factorY }))
  return [moved[0], moved[1], moved[2], moved[3]]
}

/** Shifts the quadrangle, e.g. from the visible cut-out into the whole picture. */
export function offsetQuad(quad: Quad, dx: number, dy: number): Quad {
  const moved = quad.map((corner) => ({ x: corner.x + dx, y: corner.y + dy }))
  return [moved[0], moved[1], moved[2], moved[3]]
}

/** Area of the quadrangle (shoelace formula), always positive. */
function quadArea(quad: Quad): number {
  let sum = 0
  for (let index = 0; index < 4; index += 1) {
    const current = quad[index]
    const next = quad[(index + 1) % 4]
    sum += current.x * next.y - next.x * current.y
  }
  return Math.abs(sum) / 2
}

/**
 * Side ratio of the quadrangle — long side over short side, `null` when one of
 * the two sides is degenerate.
 */
function cardAspect(quad: Quad): number | null {
  const longSide = (distance(quad[0], quad[1]) + distance(quad[3], quad[2])) / 2
  const shortSide = (distance(quad[0], quad[3]) + distance(quad[1], quad[2])) / 2
  if (!(longSide > 0 && shortSide > 0)) return null
  return Math.max(longSide, shortSide) / Math.min(longSide, shortSide)
}

/**
 * Whether the quadrangle is worth offering as a card at all (Issue #1163).
 *
 * The weaker sibling of {@link isPlausibleCard}, used by the detection itself:
 * it knows only the picture it measured in, not the frame drawn on screen, and
 * it must not throw away a card that is merely still too far away. So only the
 * two conditions that a card can never fail — the side ratio of an ID-1 card
 * and {@link SCAN_WORKER_MIN_AREA_SHARE} of the picture. Whether the card
 * really sits in the frame is decided afterwards by {@link isPlausibleCard}.
 */
export function isCardCandidate(quad: Quad, frameWidth: number, frameHeight: number): boolean {
  if (!(frameWidth > 0 && frameHeight > 0)) return false

  const aspect = cardAspect(quad)
  if (aspect === null || aspect < SCAN_ASPECT_MIN || aspect > SCAN_ASPECT_MAX) return false

  return quadArea(quad) >= frameWidth * frameHeight * SCAN_WORKER_MIN_AREA_SHARE
}

/**
 * Whether the quadrangle may be an ID-1 card lying in the frame.
 *
 * Three conditions, all of them in the coordinates of the scanned frame:
 * the side ratio fits a card, it covers most of the frame (otherwise the
 * capture would be too small to read), and no corner sticks noticeably out of
 * the frame — what is outside the frame is not stored.
 */
export function isPlausibleCard(quad: Quad, frame: CropRect): boolean {
  if (!(frame.width > 0 && frame.height > 0)) return false

  const aspect = cardAspect(quad)
  if (aspect === null || aspect < SCAN_ASPECT_MIN || aspect > SCAN_ASPECT_MAX) return false

  if (quadArea(quad) < frame.width * frame.height * SCAN_MIN_FRAME_AREA_SHARE) return false

  const marginX = frame.width * SCAN_FRAME_TOLERANCE
  const marginY = frame.height * SCAN_FRAME_TOLERANCE
  return quad.every(
    (corner) =>
      corner.x >= frame.x - marginX &&
      corner.x <= frame.x + frame.width + marginX &&
      corner.y >= frame.y - marginY &&
      corner.y <= frame.y + frame.height + marginY,
  )
}

/** Corner-wise mean of several quadrangles; `null` without a single one. */
export function averageQuads(quads: readonly Quad[]): Quad | null {
  if (quads.length === 0) return null

  const mean = (index: number): ScanPoint => ({
    x: quads.reduce((sum, quad) => sum + quad[index].x, 0) / quads.length,
    y: quads.reduce((sum, quad) => sum + quad[index].y, 0) / quads.length,
  })
  return [mean(0), mean(1), mean(2), mean(3)]
}

/**
 * Moving average over the last {@link SCAN_SMOOTH_FRAMES} detections.
 *
 * Every single detection sits a few pixels off the one before — the contour is
 * measured anew on every picture. Shown unfiltered, the outline visibly jumps
 * around the card (Issue #1146); the mean of the last few pictures lies where
 * the card is and stands still while a hand shakes.
 */
export class QuadSmoother {
  private recent: Quad[] = []

  /** The smoothed quadrangle, `null` until the first detection. */
  get quad(): Quad | null {
    return averageQuads(this.recent)
  }

  /** Forgets everything — used when the card left the frame. */
  reset(): void {
    this.recent = []
  }

  /** Takes a detection in and returns the smoothed quadrangle including it. */
  push(quad: Quad): Quad {
    this.recent = [...this.recent, quad].slice(-SCAN_SMOOTH_FRAMES)
    // Just pushed, so there is at least one — the fallback only satisfies types
    return averageQuads(this.recent) ?? quad
  }

  /** Starts over at this detection — after a move the old positions only lag. */
  restart(quad: Quad): Quad {
    this.reset()
    return this.push(quad)
  }
}

/**
 * Watches whether the card lies still.
 *
 * Measured against the smoothed mean of the last {@link SCAN_SMOOTH_FRAMES}
 * detections, not against the first one (Issue #1146): hand jitter swings
 * around a middle, so a single picture stays close to that mean while a card
 * that really travels pulls away from it. As long as every corner stays within
 * {@link SCAN_STEADY_TOLERANCE} of the frame width around the mean, the elapsed
 * time counts; anything further restarts both the mean and the clock.
 *
 * The price of the generosity is deliberate: a drift slower than the tolerance
 * per picture no longer restarts the clock. The capture straightens the outline
 * measured last and no older than {@link SCAN_QUAD_MAX_AGE_MS}, so a card that
 * has crept a few pixels is still captured where it lies.
 */
export class StabilityTracker {
  /** Frame width the tolerance is derived from; the caller sees changes to it. */
  readonly frameWidth: number

  private readonly smoother = new QuadSmoother()
  private since = 0

  constructor(frameWidth: number) {
    this.frameWidth = frameWidth
  }

  /** Forgets the detections seen so far — used when the card left the frame. */
  reset(): void {
    this.smoother.reset()
    this.since = 0
  }

  /** The smoothed quadrangle of the detections seen so far. */
  get quad(): Quad | null {
    return this.smoother.quad
  }

  /** `true` once the card has been lying still for {@link SCAN_STEADY_MS}. */
  push(quad: Quad, now: number): boolean {
    const first = this.smoother.quad === null
    const smoothed = this.smoother.push(quad)
    if (first) {
      // Nothing to compare against yet — this picture starts the clock
      this.since = now
      return false
    }

    const tolerance = this.frameWidth * SCAN_STEADY_TOLERANCE
    const steady = quad.every((corner, index) => distance(corner, smoothed[index]) <= tolerance)
    if (!steady) {
      this.smoother.restart(quad)
      this.since = now
      return false
    }
    return now - this.since >= SCAN_STEADY_MS
  }
}
